import { format } from 'date-fns';
import { setIn } from 'utilitify';

import { mapToPluralForm } from '@crm-framework/utils';

import { shortDateTimeFormat, shortDateTime } from '@crm-package/app';

import { INotificationGridItem } from './components/notification-grid';
import { INotificationFromServer } from './types';
import { NOTIFICATION_TYPES } from './consts';

export const getBalanceForDormantClientNotification = (balance: {
  [key: string]: number;
}): string =>
  Object.entries(balance).reduce((acc, [key, value]) => {
    acc += `${key}: ${value}; `;

    return acc;
  }, '');

export function mapNotificationsToGridItems(
  notifications: INotificationFromServer[]
): INotificationGridItem[] {
  return notifications.map(mapNotificationToGridItem);
}

export function mapNotificationToGridItem(
  notification: INotificationFromServer
): INotificationGridItem {
  return {
    date: format(new Date(notification.notificationDate), shortDateTimeFormat),
    notificationDetails: notification,
  };
}

export function updateNotificationGridItemsIsRead(
  notificationGridItems: INotificationGridItem[],
  isReadIds: number[]
): INotificationGridItem[] {
  return notificationGridItems.map((notificationGridItem) => {
    if (isReadIds.includes(notificationGridItem.notificationDetails.id)) {
      return setIn(notificationGridItem, true, [
        'notificationDetails',
        'isRead',
      ]);
    }

    return notificationGridItem;
  });
}

export function getNotificationMessage({
  userId,
  notificationType,
  data,
}: INotificationFromServer): string {
  const managerName =
    data.managerId && data.managerId !== userId
      ? `, Manager: ${data.managerName}`
      : '.';

  const clientInfo = data.leadId
    ? `Client ID: ${data.leadId}, Name: ${data.leadFullName},`
    : '';

  switch (notificationType) {
    case NOTIFICATION_TYPES.EconomicProfileChanged:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} filled out the Economic profile form${managerName}`;
    case NOTIFICATION_TYPES.AppropriatenessTestPassed:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} ${
        data.isPassed ? 'passed' : 'failed'
      } the Appropriateness test${managerName}`;
    case NOTIFICATION_TYPES.Deposit: {
      const cardNumber = data.cardNumber
        ? `*${data.cardNumber.replace(/\*/g, '').trim()}, `
        : '';

      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, Deposit via ${data.subType}, ${cardNumber} ${data.amount} ${data.currency}${managerName}`;
    }
    case NOTIFICATION_TYPES.DepositAttempt:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, Deposit Attempt via ${data.subType}${managerName}`;
    case NOTIFICATION_TYPES.WithdrawRequest:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, Withdraw ${data.amount} ${data.currency}${managerName}`;
    case NOTIFICATION_TYPES.WithdrawRequestAttempt:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, Withdraw Request Attempts${managerName}`;
    case NOTIFICATION_TYPES.CalendarEvent: {
      return `${clientInfo} ${data.eventName}`;
    }
    case NOTIFICATION_TYPES.NewLeadsForSales:
      return `New Leads: ${data.newLeadsIds.length}, Without attempt: ${data.newLeadsWithoutAttemptIds.length}`;
    case NOTIFICATION_TYPES.NewLeadsForTeamManagers:
      return `New Leads: ${data.newLeadsTotalCount}, Without attempt: ${data.newLeadsWithoutAttemptTotalCount}`;
    case NOTIFICATION_TYPES.ClosePnlToBalanceLimitExceeded: {
      return `${clientInfo} Close Pnl Deal: ${data.closePnlToBalancePercent}%${managerName}`;
    }
    case NOTIFICATION_TYPES.CriticalEquityExceeded:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, Critical Equity = ${data.equityPercent}%${managerName}`;
    case NOTIFICATION_TYPES.CriticalOpenPnlSumExceeded:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, Critical OpenPnl = ${data.openPnlPercent}%${managerName}`;
    case NOTIFICATION_TYPES.DocumentUploaded:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}, File name = ${data.documentName}`;
    case NOTIFICATION_TYPES.LeadAction:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName}. Message: ${data.message}`;
    case NOTIFICATION_TYPES.ExpiringDeals: {
      const { dealsCount, leadsCount } = data;
      const leadText = mapToPluralForm(leadsCount, 'client');
      const dealText = mapToPluralForm(dealsCount, 'deal');
      const dateFrom = format(new Date(data.dateFrom), shortDateTimeFormat);
      const dateTo = format(new Date(data.dateTo), shortDateTimeFormat);

      return `${dealText} of ${leadText} that expire from ${dateFrom} to ${dateTo}.`;
    }
    case NOTIFICATION_TYPES.ExpiringDealsForTeamManagers: {
      const { dealsTotalCount, leadsTotalCount, managersTotalCount } = data;
      const leadTotalText = mapToPluralForm(leadsTotalCount, 'client');
      const dealTotalText = mapToPluralForm(dealsTotalCount, 'deal');
      const managerTotalText = mapToPluralForm(managersTotalCount, 'manager');
      const dateFrom = format(new Date(data.dateFrom), shortDateTimeFormat);
      const dateTo = format(new Date(data.dateTo), shortDateTimeFormat);

      return `${dealTotalText} of ${leadTotalText} that expire from ${dateFrom} to ${dateTo} (${managerTotalText}).`;
    }
    case NOTIFICATION_TYPES.NewLeadDuplicateByPhone:
      return `Client ID ${data.duplicateLeadId}, Name ${data.duplicateLeadFullName} is duplicate by phone of original Client ID ${data.originalLeadId}, Name ${data.originalLeadFullName}.`;
    case NOTIFICATION_TYPES.UnprocessedWithdrawRequest:
      return `Client ID: ${data.leadId}, Name: ${
        data.leadFullName
      }. Unprocessed withdrawal request ${data.id} from ${format(
        new Date(data.createdDate),
        shortDateTimeFormat
      )}.`;
    case NOTIFICATION_TYPES.ClientChangedPersonalData:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} changed Personal Data${managerName}`;
    case NOTIFICATION_TYPES.IdentityDocumentExpires:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} has ${
        data.documentType
      } that expires at ${format(
        new Date(data.expirationDate),
        shortDateTime
      )}${managerName}`;
    case NOTIFICATION_TYPES.TotalDepositLimitExceeded:
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} made ${data.depositAmountPbc} ${data.pbc} Total Deposit in the last ${data.days} days${managerName}`;
    case NOTIFICATION_TYPES.NewLeadDepositCardUsed:
      return `Client ID ${data.leadId}, Name: ${
        data.leadFullName
      }, New Card * ${data.cardLast4Digits} ${data.cardExpirationMonth}/${
        data.cardExpirationYear
      } added ${format(
        new Date(data.transactionCompletedDate),
        shortDateTimeFormat
      )}${managerName}`;
    case NOTIFICATION_TYPES.NewDepositPaymentMethod: {
      const ccProcessor = data.ccProcessor
        ? `, Processor ${data.ccProcessor}`
        : '';

      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} used new method ${data.subTypeName}${ccProcessor}${managerName}.`;
    }
    case NOTIFICATION_TYPES.VideoVerificationRequested: {
      return `Client ID: ${data.leadId}, Name: ${data.leadFullName} with GUID ${data.leadGuid} is waiting for video verification.`;
    }
    case NOTIFICATION_TYPES.DormantClient: {
      return `Client ID: ${data.leadId}, Dormant Fee Commission: ${String(
        data.dormantFeeCommission
      )}, Balance: ${getBalanceForDormantClientNotification(
        data.balance
      )}, Last trading Date: ${
        data.lastTradeDate
          ? format(new Date(data.lastTradeDate), shortDateTime)
          : ''
      };`;
    }
    case NOTIFICATION_TYPES.DormantClientRestoreActive: {
      return `Client ID: ${
        data.leadId
      }, Balance: ${getBalanceForDormantClientNotification(
        data.balance
      )}, Last trading Date: ${
        data.lastTradeDate
          ? format(new Date(data.lastTradeDate), shortDateTime)
          : ''
      }`;
    }
    case NOTIFICATION_TYPES.RefillDemoAccountAttempt: {
      return `Client ID: ${data.leadId}, was trying to refill his demo account.`;
    }
    default:
      return `Unknown notification event type ${notificationType}`;
  }
}

export function deleteNotificationGridItems(
  notificationGridItems: INotificationGridItem[],
  deleteIds: number[]
): INotificationGridItem[] {
  return notificationGridItems.filter(
    (notificationGridItem) =>
      !deleteIds.includes(notificationGridItem.notificationDetails.id)
  );
}
